import { RefObject, useCallback } from 'react'
import { useTheme } from 'styled-components'

import { rowHoverStyle } from '../../DataGrid.styles'
import { CellClickArgs, ColumnConfig } from '../../DataGrid.types'
import {
  genericMemo,
  getColumnInfo,
  getColumnPinning,
  getSpannedRowIndexes,
  StickyController,
  useGlobalScopedStyle,
} from '../../utils'
import { ContentCell } from '../ContentCell'

interface RowCellsProps<RowType> {
  columns: ColumnConfig<RowType>[]
  data: RowType[]
  gridId: string
  hasDifferentPreviousValue?: boolean
  isEvenRow: boolean
  isLastRow?: boolean
  isSelected?: boolean
  onCellClick?: (cellClickArgs: CellClickArgs<RowType>) => void
  pinnedColumns: number
  pinnedColumnsRight: number
  rowData: RowType
  rowIndex: number
  stickyControllerRef: RefObject<StickyController>
  selectedColumnIndexes?: number[]
}

function _RowCells<RowType>(props: RowCellsProps<RowType>) {
  const {
    columns,
    data,
    gridId,
    hasDifferentPreviousValue,
    isEvenRow,
    isLastRow,
    isSelected,
    onCellClick,
    pinnedColumns,
    pinnedColumnsRight,
    rowData,
    rowIndex,
    stickyControllerRef,
    selectedColumnIndexes,
  } = props

  const { setStyle, clearStyle } = useGlobalScopedStyle(gridId, 'row-hovering')
  const theme = useTheme()

  const handleOnMouseOverCell = useCallback(
    (rowIndexes: number[]) => {
      setStyle(rowHoverStyle(rowIndexes, theme, !!onCellClick))
    },
    [onCellClick, setStyle, theme],
  )

  const handleOnMouseLeaveCell = useCallback(() => {
    clearStyle()
  }, [clearStyle])

  const handleCellClick = useCallback(
    (cellClickArgs: CellClickArgs<RowType>) => {
      clearStyle()
      onCellClick?.(cellClickArgs)
    },
    [clearStyle, onCellClick],
  )

  let hoverRowsList = [rowIndex]
  const spannedColumnIndex = columns.findIndex(column => column.shouldSpanRowWithSameValue)
  return columns.map((column, columnIndex) => {
    const {
      isLastColumn,
      isLastPinnedLeftColumn,
      isLastColumnInTheGroup,
      isFirstPinnedRightColumn,
    } = getColumnInfo({
      columnIndex,
      columns,
      pinnedColumns,
      pinnedColumnsRight,
    })

    let spanCount = 1

    if (column.shouldSpanRowWithSameValue) {
      // Get index list of the rows that have same value that are neighboor
      hoverRowsList = getSpannedRowIndexes(rowIndex, data, column.columnKey as keyof RowType)

      // Not render value if column has same value as before
      if (!hasDifferentPreviousValue) {
        return null
      }

      spanCount = hoverRowsList.length
    }
    const isCellInGroupRow = !hasDifferentPreviousValue && columnIndex >= spannedColumnIndex

    const isColumnSelected = selectedColumnIndexes?.some(index => index === columnIndex)

    return (
      <ContentCell
        column={column}
        columnIndex={columnIndex}
        columnPinning={getColumnPinning(columnIndex, pinnedColumns, pinnedColumnsRight, columns)}
        gridId={gridId}
        isCellInGroupedRow={isCellInGroupRow}
        isEvenRow={isEvenRow}
        isLastColumn={isLastColumn}
        isLastRow={isLastRow}
        isSelected={isSelected || isColumnSelected}
        isInteractive={!!onCellClick}
        key={`${column.columnKey}-${rowIndex}`}
        onClick={handleCellClick}
        onMouseLeave={handleOnMouseLeaveCell}
        onMouseOver={() => handleOnMouseOverCell(hoverRowsList)}
        rowData={rowData}
        rowIndex={rowIndex}
        spanCount={spanCount === 1 ? undefined : spanCount}
        stickyControllerRef={stickyControllerRef}
        withLeftBorder={isFirstPinnedRightColumn}
        withRightBorder={isLastPinnedLeftColumn || isLastColumnInTheGroup}
      />
    )
  })
}

export const RowCells = genericMemo(_RowCells)
